import type { TimsTrip, Vehicle, VehicleMeta } from './types'

const INDIAN_PLATE = /[A-Z]{2}\d{2}[A-Z]{1,3}\d{4}/
const MIN_PREFIX = 8

export function normalizePlate(raw: string): string {
  return raw.replace(/[^A-Z0-9]/gi, '').toUpperCase()
}

export function plateKeys(raw: string | null | undefined): Set<string> {
  const n = normalizePlate(raw ?? '')
  const keys = new Set<string>()
  if (!n) return keys
  keys.add(n)
  const m = n.match(INDIAN_PLATE)
  if (m?.[0]) keys.add(m[0])
  return keys
}

function matchScore(needles: Set<string>, hay: Set<string>, rosterNorm: string): number {
  if (needles.has(rosterNorm)) return 4
  for (const n of needles) {
    if (hay.has(n)) return 3
  }
  for (const n of needles) {
    for (const h of hay) {
      if (n.length >= MIN_PREFIX && h.length >= MIN_PREFIX
        && (n.startsWith(h) || h.startsWith(n))) {
        return 2
      }
    }
  }
  return 0
}

/** Resolve a fleet plate to its TIMS overlay (exact key, then fuzzy plate match). */
export function resolveTimsTrip(
  vehicleId: string,
  trips: ReadonlyMap<string, TimsTrip>,
): TimsTrip | undefined {
  const direct = trips.get(vehicleId)
  if (direct) return direct
  const needles = plateKeys(vehicleId)
  if (!needles.size) return undefined
  let best: { score: number; len: number; trip: TimsTrip } | undefined
  for (const [key, trip] of trips) {
    for (const token of [key, trip.vehicle_id, trip.tims_vehicle]) {
      if (!token) continue
      const hay = plateKeys(token)
      const score = matchScore(needles, hay, normalizePlate(token))
      if (!score) continue
      const cand = { score, len: normalizePlate(token).length, trip }
      if (!best || cand.score > best.score
        || (cand.score === best.score && cand.len > best.len)) {
        best = cand
      }
    }
  }
  return best?.trip
}

/** One entry per TIMS trip (dedupe alias keys). */
export function uniqueTimsTrips(trips: ReadonlyMap<string, TimsTrip>): TimsTrip[] {
  const seen = new Set<TimsTrip>()
  const out: TimsTrip[] = []
  for (const trip of trips.values()) {
    if (seen.has(trip)) continue
    seen.add(trip)
    out.push(trip)
  }
  return out
}

/** Synthetic roster row for a TIMS trip with no live GPS vehicle yet. */
export function timsOnlyVehicle(vehicleId: string): Vehicle {
  return {
    vehicle_id: vehicleId,
    device_id: null,
    updated_ts: 0,
    last_obd_ts: 0,
    last_geo_ts: 0,
    lat: null,
    lon: null,
    speed_kph: null,
    heading_deg: null,
    satellites: null,
    accuracy_level: null,
    soc_pct: null,
    coolant_c: null,
    mcu_temp_c: null,
    motor_rpm: null,
    odo_raw: null,
    odo_km: null,
    pack_voltage: null,
    pack_current: null,
    power_kw: null,
    energy_kwh_session: null,
    discharge_kwh_session: null,
    regen_kwh_session: null,
    idle_kwh_session: null,
    charge_kwh_session: null,
    odo_km_session: null,
    gps_km_session: null,
    live_kwh_per_km: null,
    battery_state: null,
    charger_state: null,
    moving: false,
    obd_frames: 0,
    geo_fixes: 0,
    sentinel_count: 0,
    obd_lag_sec: null,
    geo_lag_sec: null,
  }
}

/** TIMS driver wins when present; otherwise Kafka vehicle.meta. */
export function overlayDriver(
  trip: TimsTrip | undefined,
  meta: VehicleMeta | undefined,
): string | null {
  const name = trip?.driver_name?.trim() || meta?.driver_name?.trim()
  return name || null
}

export function overlayDriverId(
  trip: TimsTrip | undefined,
  meta: VehicleMeta | undefined,
): string | null {
  const id = trip?.driver_id?.trim() || meta?.driver_id?.trim()
  return id || null
}

export function fmtTonnage(v: TimsTrip['tonnage']): string | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isFinite(n)) return `${n} t`
  return String(v)
}

/**
 * TIMS trip tonnage present on a STILL-OPEN trip → vehicle is carrying cargo now
 * (Loaded for UI, and for isLaden()'s load-direction/efficiency classification).
 *
 * trip_closed IS CHECKED, not just tonnage. An LR is issued for the laden leg only --
 * the empty return has no new TIMS trip to replace it, so index_trips (serving/
 * tims_trips.py) deliberately keeps the closed laden trip as "the current trip" for
 * that plate, purely so the card still shows who last drove. Read here as "cargo is on
 * board right now" instead, that same closed trip kept a truck LADEN (and on the
 * corridor's ~2.1 kWh/km laden efficiency, not its ~1.1 kWh/km empty one) for its
 * entire empty run back -- overriding even a live, GPS-confirmed 'dhar-dhule-empty'
 * route_id, which isLaden() otherwise trusts over every other signal. estimateRange()
 * then roughly doubled the assumed energy draw for most of the fleet at any moment
 * (every truck mid-return), producing "won't reach DHAR" / low-expected-SoC range-risk
 * alarms for trucks that were in fact running fine, empty. Confirmed: most Ultratech
 * ETA/arrival-SoC figures reported wrong, matching this exact mechanism.
 */
export function hasTimsTonnage(trip: TimsTrip | undefined): boolean {
  if (!trip || trip.trip_closed) return false
  const v = trip.tonnage
  if (v == null || v === '') return false
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isFinite(n)) return n > 0
  return String(v).trim().length > 0
}

/** Structural / transport keys — never shown to operators. */
const HIDDEN = new Set([
  'vehicle_id', 'tims_vehicle', 'details', 'raw', 'entry_ts', 'entry_source',
  'vehicle_no', 'log_event_code', 'vendor_name', 'shift_name', 'trailer_no',
])

/** Explicit PK / FK keys from TIMS (also caught by `_id` suffix rule). */
const HIDDEN_IDS = new Set([
  'id', 'trip_id', 'driver_id', 'trailer_id', 'log_id',
  'trip_stage_event_id', 'stage_event_id',
])

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** True when a TIMS field key is an internal id / FK — hide on cards and detail grids. */
export function isTimsOperatorHiddenField(key: string): boolean {
  if (HIDDEN.has(key) || HIDDEN_IDS.has(key)) return true
  if (key === 'id' || key.endsWith('_id')) return true
  return false
}

function looksLikeInternalId(v: unknown): boolean {
  if (v == null || v === '') return false
  const s = String(v).trim()
  if (!s) return false
  if (UUID_RE.test(s)) return true
  return /^\d{6,}$/.test(s)
}

const LABEL: Record<string, string> = {
  lr_number: 'LR',
  di_number: 'DI',
  driver_name: 'driver',
  vehicle_type: 'vehicle type',
  trip_stage_event: 'stage event',
  is_trip_completed: 'trip completed',
  is_exchange: 'exchange',
  delay_min: 'delay (min)',
  delay_formatted: 'delay',
  delay_reason: 'delay reason',
  flag_status: 'TIMS flag status',
  badge_label: 'badge',
  flag_count: 'TIMS flags',
  in_time: 'log time',
  in_odo: 'odo',
  in_soc: 'SoC',
  log_created_at: 'log created',
  transaction_time: 'transaction time',
  transaction_odo: 'transaction odo',
  transaction_soc: 'transaction SoC',
  trip_completed_time: 'completed at',
  trip_date: 'trip date',
  site_name: 'site',
}

const ORDER = [
  'driver_name', 'lr_number', 'trailer', 'vendor', 'event', 'shift',
  'di_number', 'vehicle_type', 'tonnage', 'site_name', 'trip_date',
  'trip_stage_event', 'in_time', 'in_odo', 'in_soc', 'delay_formatted',
  'delay_min', 'delay_reason', 'is_trip_completed', 'is_exchange',
  'flag_status', 'badge_label', 'flag_count', 'log_created_at',
  'trip_completed_time', 'status',
]

/** Show a dash when the API had the key but the value is empty. */
const IMPORTANT_EMPTY = new Set(['lr_number', 'trailer', 'di_number'])

export interface TimsField {
  label: string
  value: string
}

function isEmpty(v: unknown): boolean {
  return v == null || v === ''
}

/** TIMS timestamp fields that mix two shapes on the same trip record (see
 *  fmtTimsTimestamp below) and so need normalizing before they're shown together. */
const TIMESTAMP_KEYS = new Set(['in_time', 'log_created_at', 'trip_completed_time', 'transaction_time'])
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function hasExplicitOffset(v: string): boolean {
  return /Z$|[+-]\d{2}:?\d{2}$/.test(v.trim())
}

/**
 * in_time comes straight from TIMS's own transaction log, which stamps a naive
 * "YYYY-MM-DD HH:mm:ss" with no offset -- confirmed already IST wall-clock against
 * gate logs. log_created_at/trip_completed_time come from a different part of the
 * same payload and carry an explicit UTC offset. Rendered verbatim side by side on
 * one trip card, the same trip reads as if its own gate-in and completion were hours
 * apart in different zones. Normalize both shapes to one IST-labeled format so every
 * timestamp on the card reads the same way.
 */
function fmtTimsTimestamp(v: string): string {
  if (hasExplicitOffset(v)) {
    const d = new Date(v)
    if (!Number.isNaN(d.getTime())) {
      const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })
      const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' })
      return `${date}, ${time} IST`
    }
  }
  const m = v.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/)
  if (m) {
    const [, , mo, da, hh, mm] = m
    return `${da} ${MONTHS[Number(mo) - 1]}, ${hh}:${mm} IST`
  }
  return v
}

function fmtValue(key: string, v: unknown): string {
  if (isEmpty(v)) return '—'
  if (key === 'tonnage') return fmtTonnage(v as TimsTrip['tonnage']) ?? '—'
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  if (TIMESTAMP_KEYS.has(key) && typeof v === 'string') return fmtTimsTimestamp(v)
  return String(v)
}

function labelOf(key: string): string {
  return LABEL[key] ?? key.replace(/_/g, ' ')
}

function emptySourceExists(key: string, details: Record<string, unknown>): boolean {
  if (key === 'trailer') return 'trailer_no' in details || 'trailer' in details
  return key in details
}

/**
 * 2-col TIMS card rows: every trip/details key, skipping empty values except
 * important blanks (LR / trailer / DI) when the API actually sent the key.
 */
export function timsDetailFields(
  trip: TimsTrip,
  extras?: { driverName?: string | null },
): TimsField[] {
  const details: Record<string, unknown> = { ...(trip.details ?? {}) }
  const bag: Record<string, unknown> = { ...details }

  const named: (keyof TimsTrip)[] = [
    'driver_name', 'lr_number', 'trailer', 'vendor', 'vehicle_type',
    'tonnage', 'event', 'shift', 'di_number', 'in_time', 'in_odo', 'in_soc',
    'status', 'site_name', 'trip_date', 'delay_min', 'delay_formatted',
    'delay_reason', 'is_trip_completed', 'is_exchange', 'flag_status',
    'badge_label', 'flag_count', 'trip_stage_event', 'trip_completed_time',
  ]
  for (const k of named) {
    const v = trip[k]
    if (v !== undefined) bag[k as string] = v
  }
  if (extras?.driverName) bag.driver_name = extras.driverName

  if ('vendor' in bag) delete bag.vendor_name
  if ('shift' in bag) delete bag.shift_name
  if ('trailer' in bag) delete bag.trailer_no
  if ('event' in bag) delete bag.log_event_code
  if (bag.in_time != null && bag.in_time !== '') delete bag.transaction_time
  if (bag.in_odo != null && bag.in_odo !== '') delete bag.transaction_odo
  if (bag.in_soc != null && bag.in_soc !== '') delete bag.transaction_soc
  delete bag.vehicle_no
  for (const h of HIDDEN) delete bag[h]

  // badge_label/delay_formatted are presentational strings TIMS derives from the route
  // (e.g. "Dhar-Jhul Delay") -- they describe which leg a delay would apply to, not
  // whether one is actually happening. Showing them regardless of delay_min reads as an
  // active-delay badge even when the trip is on time. Only show them once delay_min
  // confirms there actually is a delay.
  const delayMin = typeof bag.delay_min === 'number' ? bag.delay_min : Number(bag.delay_min)
  const hasDelay = Number.isFinite(delayMin) && delayMin > 0
  if (!hasDelay) {
    delete bag.badge_label
    delete bag.delay_formatted
  }
  // flag_status is TIMS's own per-trip flag indicator, not necessarily a delay flag, so
  // it is NOT gated on hasDelay alone -- that would hide a genuine non-delay flag. But
  // when TIMS reports a flag_status with neither an active delay nor a positive flag_count
  // behind it, the card reads as "flagged" while every number next to it says nothing is
  // wrong, which is the same contradiction badge_label/delay_formatted were fixed for
  // above. Drop it only in that specific unsupported case.
  const flagCount = typeof bag.flag_count === 'number' ? bag.flag_count : Number(bag.flag_count)
  const hasFlagCount = Number.isFinite(flagCount) && flagCount > 0
  if (!hasDelay && !hasFlagCount) {
    delete bag.flag_status
  }

  const keys: string[] = []
  const seen = new Set<string>()
  for (const k of ORDER) {
    if (k in bag && !seen.has(k) && !isTimsOperatorHiddenField(k)) {
      keys.push(k)
      seen.add(k)
    }
  }
  for (const k of Object.keys(bag)) {
    if (!seen.has(k) && !isTimsOperatorHiddenField(k)) {
      keys.push(k)
      seen.add(k)
    }
  }

  const fields: TimsField[] = []
  for (const k of keys) {
    const v = bag[k]
    if (isTimsOperatorHiddenField(k)) continue
    if (!isEmpty(v) && looksLikeInternalId(v) && !(k in LABEL)) continue
    if (isEmpty(v)) {
      if (!IMPORTANT_EMPTY.has(k) || !emptySourceExists(k, details)) continue
    }
    fields.push({ label: labelOf(k), value: fmtValue(k, v) })
  }
  return fields
}

function parseTs(v: unknown): number {
  if (v == null || v === '') return 0
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v > 1e12 ? v / 1000 : v
  }
  const s = String(v).trim()
  if (!s) return 0
  const t = Date.parse(s.includes('T') || s.includes(' ') ? s : `${s}T00:00:00Z`)
  return Number.isFinite(t) ? t / 1000 : 0
}

/**
 * Unix seconds of the newest TIMS entry. Serving sets `entry_ts` as max of
 * transaction.time, logs[].created_at, updated_at/created_at, in_time, trip_date.
 * Client falls back to those overlay fields if the poll hasn't landed yet.
 */
export function tripEntryTs(trip: TimsTrip | undefined): number {
  if (!trip) return 0
  if (typeof trip.entry_ts === 'number' && Number.isFinite(trip.entry_ts) && trip.entry_ts > 0) {
    return trip.entry_ts
  }
  const d = trip.details ?? {}
  return Math.max(
    parseTs(d.transaction_time),
    parseTs(d.log_created_at),
    parseTs(trip.in_time),
    parseTs(trip.trip_completed_time),
    parseTs(trip.trip_date),
  )
}

/**
 * TIMS-matched rows first, then `fallback` decides order within each group, falling back
 * further to newest entry_ts only when `fallback` itself reports a tie.
 *
 * `fallback` USED TO BE THE LAST RESORT, not the first: entry_ts recency was checked
 * before it, and on this corridor two TIMS-matched rows sharing the exact same entry_ts is
 * rare enough that `fallback` -- the FLEET tab's own user-selected sort key (Speed/Latest/
 * SOC/Breakdown) -- was reached for almost no pair of rows. Picking "Speed" reordered
 * almost nothing, which read as "sorting doesn't work." The one caller of this function is
 * that sort control; entry_ts recency is a reasonable tiebreak for whatever the user chose,
 * never a replacement for it.
 */
export function compareTimsFirst<T>(
  a: T,
  b: T,
  tripOf: (row: T) => TimsTrip | undefined,
  fallback: (a: T, b: T) => number,
): number {
  const ta = tripOf(a)
  const tb = tripOf(b)
  const aHas = !!ta
  const bHas = !!tb
  if (aHas !== bHas) return aHas ? -1 : 1
  const fb = fallback(a, b)
  if (fb !== 0) return fb
  return aHas && bHas ? tripEntryTs(tb) - tripEntryTs(ta) : 0
}
